/**
 * Normalized API failure. Every non-2xx response becomes one of these, so callers render
 * errors the same way everywhere and never touch a raw Response.
 */
export class ApiError extends Error {
  readonly status: number;

  /** Field-level messages from a ValidationProblemDetails, keyed by camelCase field name. */
  readonly fields: Record<string, string[]>;

  /** Correlation id from the response header, shown to the user so support can trace it. */
  readonly correlationId: string | null;

  /**
   * Seconds the caller must wait, on a 429 and nowhere else.
   *
   * Read from the problem's own `retryAfterSeconds` rather than parsed out of the message:
   * a client that needs the number must not have to read prose to find it, and the sentence
   * is free to be rewritten without breaking a countdown.
   */
  readonly retryAfterSeconds: number | null;

  constructor(
    message: string,
    status: number,
    fields: Record<string, string[]> = {},
    correlationId: string | null = null,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fields = fields;
    this.correlationId = correlationId;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** True when the caller is being throttled and must wait. */
  get isThrottled(): boolean {
    return this.status === 429;
  }

  /** True when the caller is authenticated but lacks the permission. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidation(): boolean {
    return this.status === 400 && Object.keys(this.fields).length > 0;
  }

  /** First message for a field, for rendering under the input that caused it. */
  /**
   * The first message the server returned for one field, matched CASE-INSENSITIVELY.
   *
   * **The casing on the wire is not one thing, and that is why this normalises.** A
   * FluentValidation failure goes through ASP.NET's own model-validation pipeline and arrives
   * camel-cased — `classificationId`. A failure a SERVICE returns is a dictionary the product
   * builds itself from `nameof(request.ClassificationId)`, and `ValidationProblemDetails.Errors`
   * is a dictionary: `PropertyNamingPolicy` renames POCO properties, never dictionary KEYS, and
   * `DictionaryKeyPolicy` is not set. So that half arrives Pascal-cased.
   *
   * Matched exactly, every service-returned field error missed its field — the banner said what
   * was wrong, the input stayed silent, and `focusValidationError` moved nothing. That is the
   * failure mode its own doc comment calls out, one layer earlier: the reader is told which
   * field is wrong and nothing happens, which reads as the feature not being wired.
   *
   * Normalising HERE rather than at the call sites is the point — a caller cannot know which
   * half of the pipeline rejected it, and asking forty forms to guess is asking one of them to
   * guess wrong.
   */
  fieldError(name: string): string | undefined {
    const exact = this.fields[name]?.[0];

    if (exact !== undefined) {
      return exact;
    }

    const wanted = name.toLowerCase();

    for (const key of Object.keys(this.fields)) {
      if (key.toLowerCase() === wanted) {
        return this.fields[key]?.[0];
      }
    }

    return undefined;
  }
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
  /** Present on a 429. The `Retry-After` header carries the same number. */
  retryAfterSeconds?: number;
}

/**
 * The wait a 429 asks for, from the body and then the header.
 *
 * Both are read because they can disagree in one direction that matters: a proxy may strip or
 * rewrite `Retry-After`, and the body survives that. A value that is not a positive finite
 * number is dropped rather than defaulted — a countdown is only worth drawing when the server
 * actually said how long.
 */
function parseRetryAfter(problem: ProblemDetails, response: Response): number | null {
  const candidates = [problem.retryAfterSeconds, Number(response.headers.get('Retry-After'))];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.ceil(value);
    }
  }

  return null;
}

export async function parseApiError(response: Response): Promise<ApiError> {
  const correlationId = response.headers.get('X-Correlation-Id');

  let problem: ProblemDetails = {};

  try {
    const text = await response.text();
    if (text) {
      problem = JSON.parse(text) as ProblemDetails;
    }
  } catch {
    // Not JSON — fall through to the status-based message below.
  }

  const fields = problem.errors ?? {};

  /*
    A validation failure names the fields, always.

    ASP.NET's ValidationProblemDetails carries a title of "One or more validation errors
    occurred." and puts the useful part in `errors`. Preferring the title showed the reader a
    sentence that told them something went wrong and nothing about what — they had to guess
    which field, on a form that had just refused them.

    The per-field messages stay in `fields` for rendering under the inputs; this is what a
    surface shows when it has only one place to put an error.
  */
  const fieldMessages = Object.values(fields).flat().filter(Boolean);

  const message = fieldMessages.length > 0
    ? fieldMessages.join(' ')
    : problem.detail || problem.title || defaultMessageFor(response.status);

  return new ApiError(
    message,
    response.status,
    fields,
    correlationId,
    response.status === 429 ? parseRetryAfter(problem, response) : null,
  );
}

/**
 * Messages are written for the person reading them, not for a developer. They say what
 * happened and what to do, and never expose server internals.
 */
function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return 'Some of the information supplied is not valid.';
    case 401:
      return 'Your session has ended. Sign in again to continue.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That record no longer exists.';
    case 409:
      return 'That change conflicts with the current state of the record.';
    case 429:
      return 'Too many attempts. Wait a moment and try again.';
    case 500:
      return 'Something went wrong on our side. Try again, and contact support if it continues.';
    default:
      return status >= 500
        ? 'The service is temporarily unavailable. Try again shortly.'
        : 'The request could not be completed.';
  }
}
